import { createSelector } from "reselect";
/*
import {
    createSelectorWithDependencies as createSelector
} from 'reselect-tools';
*/
import { ActionReducerMap } from "@ngrx/store";

import * as fromAuth from "./auth/auth.reducer";
import * as fromPatients from "./patient/patient.reducer";
import * as fromDocuments from "./document/document.reducer";
import * as fromDescriptions from "./description/description.reducer";
import * as fromPlans from "./plan/plan.reducer";
import * as fromOdontogram from "./odontogram/odontogram.reducer";
import * as fromPlanEditor from "./plan-editor/plan-editor.reducer";
import * as fromDiagnosis from "./plan-editor/diagnosis/diagnosis.reducer";
import * as fromPlan from "./plan-editor/plan/plan.reducer";
import * as fromReport from "./plan-editor/report/report.reducer";
import * as fromPrices from "./price/price.reducer";
import * as fromUser from "./user/user.reducer";
import * as fromAccount from "./account/account.reducer";
import * as fromCustomTreatment from "./custom-treatment/custom-treatment.reducer";

export interface AppState {
  auth: fromAuth.State;
  patients: fromPatients.State;
  documents: fromDocuments.State;
  descriptions: fromDescriptions.State;
  plans: fromPlans.State;
  diagnosis: fromDiagnosis.State;
  plan: fromPlan.State;
  report: fromReport.State;
  odontogram: fromOdontogram.State;
  plan_editor: fromPlanEditor.State;
  prices: fromPrices.State;
  account: fromAccount.State;
  user: fromUser.State;
  custom_treatment: fromCustomTreatment.State;
}

export const reducers: ActionReducerMap<AppState> = {
  auth: fromAuth.reducer,
  patients: fromPatients.reducer,
  documents: fromDocuments.reducer,
  descriptions: fromDescriptions.reducer,
  plans: fromPlans.reducer,
  diagnosis: fromDiagnosis.reducer,
  plan: fromPlan.reducer,
  report: fromReport.reducer,
  odontogram: fromOdontogram.reducer,
  plan_editor: fromPlanEditor.reducer,
  prices: fromPrices.reducer,
  account: fromAccount.reducer,
  user: fromUser.reducer,
  custom_treatment: fromCustomTreatment.reducer,
};

// ==== auth states and compose methods ====

export const getAuthState = (state: AppState) => state.auth;

export const getCurrentUser = createSelector(
  getAuthState,
  fromAuth.getCurrentUser,
);
export const getIsLogged = createSelector(getAuthState, fromAuth.getIsLogged);
export const getLoginData = createSelector(getAuthState, fromAuth.getLoginData);
export const getRegisterData = createSelector(
  getAuthState,
  fromAuth.getRegisterData,
);
export const getResetPasswordData = createSelector(
  getAuthState,
  fromAuth.getResetPasswordData,
);
export const getRecoverPasswordData = createSelector(
  getAuthState,
  fromAuth.getRecoverPasswordData,
);
export const getAuthIsLoading = createSelector(
  getAuthState,
  fromAuth.getIsloading,
);

// ==== patients states and compose methods ====

export const getPatientsState = (state: AppState) => state.patients;

export const getPatients = createSelector(
  getPatientsState,
  fromPatients.getPatients,
);
export const getSelectedPatient = createSelector(
  getPatientsState,
  fromPatients.getSelected,
);
export const getIsSearchingPatient = createSelector(
  getPatientsState,
  fromPatients.isSearching,
);
export const getIsLoadingPatient = createSelector(
  getPatientsState,
  fromPatients.isLoading,
);
export const getPatientSearchQuery = createSelector(
  getPatientsState,
  fromPatients.getSearchQuery,
);
export const getPatientShowDetails = createSelector(
  getPatientsState,
  fromPatients.getShowDetails,
);

// ==== documents states and compose methods ====

export const getDocumentsState = (state: AppState) => state.documents;

export const getDocuments = createSelector(
  getDocumentsState,
  fromDocuments.getDocuments,
);
export const getSelectedDocument = createSelector(
  getDocumentsState,
  fromDocuments.getSelected,
);
export const getIsSearchingDocument = createSelector(
  getDocumentsState,
  fromDocuments.isSearching,
);
export const getIsLoadingDocument = createSelector(
  getDocumentsState,
  fromDocuments.isLoading,
);
export const getDocumentSearchQuery = createSelector(
  getDocumentsState,
  fromDocuments.getSearchQuery,
);
export const getDocumentShowDetails = createSelector(
  getDocumentsState,
  fromDocuments.getShowDetails,
);

// ==== descriptions states and compose methods =====

export const getDescriptionsState = (state: AppState) => state.descriptions;

export const getDescriptions = createSelector(
  getDescriptionsState,
  fromDescriptions.getDescriptions,
);
export const getSelectedDescription = createSelector(
  getDescriptionsState,
  fromDescriptions.getSelected,
);
export const getIsSearchingDescription = createSelector(
  getDescriptionsState,
  fromDescriptions.isSearching,
);
export const getIsLoadingDescription = createSelector(
  getDescriptionsState,
  fromDescriptions.isLoading,
);
export const getDescriptionSearchQuery = createSelector(
  getDescriptionsState,
  fromDescriptions.getSearchQuery,
);
export const getDescriptionShowDetails = createSelector(
  getDescriptionsState,
  fromDescriptions.getShowDetails,
);
export const getDescriptionType = createSelector(
  getDescriptionsState,
  fromDescriptions.getType,
);

// ==== plans states and compose methods ====

export const getPlansState = (state: AppState) => state.plans;

export const getPlans = createSelector(getPlansState, fromPlans.getPlans);
export const getIsSearchingPlan = createSelector(
  getPlansState,
  fromPlans.isSearching,
);
export const getIsLoadingPlan = createSelector(
  getPlansState,
  fromPlans.isLoading,
);
export const getPlanSearchQuery = createSelector(
  getPlansState,
  fromPlans.getSearchQuery,
);
export const getPlanSearchPatient = createSelector(
  getPlansState,
  fromPlans.getSearchPatient,
);
export const getCurrentPlan = createSelector(
  getPlansState,
  fromPlans.getCurrentPlan,
);
export const getCurrentWizardPlan = createSelector(
  [getCurrentPlan, getSelectedPatient],
  (plan, patient) => {
    return Object.assign({}, plan, { patient: patient });
  },
);

// ==== odontogram states and compose methods ====

export const getOdontogramState = (state: AppState) => state.odontogram;

const odontogramTeeth = createSelector(
  getOdontogramState,
  (state) => state.teeth,
);
const odontogramSelectedTeeth = createSelector(
  getOdontogramState,
  (state) => state.selectedTeeth,
);

export const getOdontogram = createSelector(
  odontogramTeeth,
  odontogramSelectedTeeth,
  (teeth, selectedTeeth) =>
    teeth.map((tooth) => {
      tooth.selected = selectedTeeth.indexOf(tooth.number) > -1;
      return tooth;
    }),
);
export const getOdontogramQ1Selected = createSelector(
  getOdontogramState,
  fromOdontogram.getQ1Selected,
);
export const getOdontogramQ2Selected = createSelector(
  getOdontogramState,
  fromOdontogram.getQ2Selected,
);
export const getOdontogramQ3Selected = createSelector(
  getOdontogramState,
  fromOdontogram.getQ3Selected,
);
export const getOdontogramQ4Selected = createSelector(
  getOdontogramState,
  fromOdontogram.getQ4Selected,
);
export const getSelectedTeeth = createSelector(
  getOdontogramState,
  fromOdontogram.getSelectedTeeth,
);

// ==== plan editor states and compose methods ====

export const getPlanEditorState = (state: AppState) => state.plan_editor;

export const getPlanEditorCurrentPlanId = createSelector(
  getPlanEditorState,
  fromPlanEditor.getPlanId,
);
export const getPlanEditorCurrentPlanName = createSelector(
  getPlanEditorState,
  fromPlanEditor.getPlanName,
);
export const getPlanEditorCurrentPatient = createSelector(
  getPlanEditorState,
  fromPlanEditor.getPatient,
);

// ==== diagnosis states and compose methods ====

export const getDiagnosisState = (state: AppState) => state.diagnosis;

export const getDiagnosisCurrentGroupIdentifier = createSelector(
  getDiagnosisState,
  fromDiagnosis.getCurrentGroupIdentifier,
);
export const getDiagnosisCurrentConditionIdentifier = createSelector(
  getDiagnosisState,
  fromDiagnosis.getCurrentConditionIdentifier,
);
export const getDiagnosisCurrentItem = createSelector(
  getDiagnosisState,
  fromDiagnosis.getCurrentItem,
);
export const getDiagnosisItems = createSelector(
  getDiagnosisState,
  fromDiagnosis.getItems,
);
export const getDiagnosisCurrentItemFull = createSelector(
  [getDiagnosisCurrentItem, getSelectedTeeth],
  (currentItem, selectedTeeth) => {
    return Object.assign({}, currentItem, { teeth: selectedTeeth });
  },
);
export const getDiagnosisShowItemList = createSelector(
  getDiagnosisState,
  fromDiagnosis.getShowItemList,
);
export const getDiagnosisUseOdontogram = createSelector(
  getDiagnosisState,
  fromDiagnosis.getUseOdontogram,
);
export const getDiagnosisMainComponent = createSelector(
  getDiagnosisState,
  fromDiagnosis.getMainComponent,
);
export const getDiagnosisSaveAction = createSelector(
  getDiagnosisState,
  fromDiagnosis.getSaveAction,
);
export const getDiagnosis360 = createSelector(
  getDiagnosisState,
  fromDiagnosis.getDiagnosis360,
);
export const getDiagnosisOrthoMalocclusions = createSelector(
  getDiagnosisState,
  fromDiagnosis.getOrthoMalocclusions,
);
export const getDiagnosisOrthoFacial = createSelector(
  getDiagnosisState,
  fromDiagnosis.getOrthoFacial,
);
export const getDiagnosisOrthoTMJ = createSelector(
  getDiagnosisState,
  fromDiagnosis.getOrthoTMJ,
);
export const getDiagnosisIsLoading = createSelector(
  getDiagnosisState,
  fromDiagnosis.getIsLoading,
);

// ==== plan states and compose methods ====

export const getPlanState = (state: AppState) => state.plan;

export const getPlanCurrentGroupIdentifier = createSelector(
  getPlanState,
  fromPlan.getCurrentGroupIdentifier,
);
export const getPlanCurrentTreatmentIdentifier = createSelector(
  getPlanState,
  fromPlan.getCurrentTreatmentIdentifier,
);
export const getPlanCurrentItem = createSelector(
  getPlanState,
  fromPlan.getCurrentItem,
);
export const getPlanStages = createSelector(getPlanState, fromPlan.getStages);
export const getPlanCustomTotalText = createSelector(
  getPlanState,
  fromPlan.getCustomTotalText,
);

export const getPlanDiscount = createSelector(
  getPlanState,
  fromPlan.getDiscount,
);

export const getPlanItems = createSelector(getPlanStages, (stages) => {
  return [].concat.apply(
    [],
    stages.map((stage) => stage.items),
  );
});
export const getPlanCurrentItemFull = createSelector(
  [getPlanCurrentItem, getSelectedTeeth],
  (currentItem, selectedTeeth) => {
    // currentItem.teeth = selectedTeeth;
    // return new object so we can detect the changes
    return Object.assign({}, currentItem, { teeth: selectedTeeth });
  },
);
export const getPlanShowItemList = createSelector(
  getPlanState,
  fromPlan.getShowItemList,
);
export const getPlanUseOdontogram = createSelector(
  getPlanState,
  fromPlan.getUseOdontogram,
);
export const getPlanMainComponent = createSelector(
  getPlanState,
  fromPlan.getMainComponent,
);
export const getPlanIsLoading = createSelector(
  getPlanState,
  fromPlan.getIsLoading,
);

// ==== report states and compose methods ====

export const getReportState = (state: AppState) => state.report;

export const getIsGeneratingReport = createSelector(
  getReportState,
  fromReport.isGenerating,
);
export const getIsReportUpdated = createSelector(
  getReportState,
  fromReport.isUpdated,
);
export const getReportConfiguration = createSelector(
  getReportState,
  fromReport.getConfiguration,
);
export const getReportPdfPath = createSelector(
  getReportState,
  fromReport.getPdfPath,
);
export const getReportDocuments = createSelector(
  getReportState,
  fromReport.getDocuments,
);

// ==== prices states and compose methods ====

export const getPricesState = (state: AppState) => state.prices;

export const getPrices = createSelector(getPricesState, fromPrices.getPrices);
export const getIsLoadingPrices = createSelector(
  getPricesState,
  fromPrices.isLoading,
);

// ==== settings states and compose methods ====

export const getUserState = (state: AppState) => state.user;

export const getSettings = createSelector(getUserState, fromUser.getSettings);
export const getIsLoadingSettings = createSelector(
  getUserState,
  fromUser.isLoading,
);
export const getHasActiveSubscription = createSelector(
  getUserState,
  fromUser.hasSubscriptionActive,
);

// ==== subscription states and compose methods ====

export const getAccountState = (state: AppState) => state.account;

export const getSubscriptions = createSelector(
  getAccountState,
  fromAccount.getSubscriptions,
);
export const getBillingInfo = createSelector(
  getAccountState,
  fromAccount.getBillingInfo,
);
export const getAppliedCoupon = createSelector(
  getAccountState,
  fromAccount.getAppliedCoupon,
);
export const getIsLoadingAccount = createSelector(
  getAccountState,
  fromAccount.isLoading,
);

// ==== custom treatment states and compose methods ====

export const getCustomTreatmentsState = (state: AppState) =>
  state.custom_treatment;

export const getCustomTreatments = createSelector(
  getCustomTreatmentsState,
  fromCustomTreatment.getCustomTreatments,
);
export const getSelectedCustomTreatment = createSelector(
  getCustomTreatmentsState,
  fromCustomTreatment.getSelected,
);
export const getIsLoadingCustomTreatment = createSelector(
  getCustomTreatmentsState,
  fromCustomTreatment.isLoading,
);
export const getCustomTreatmentshowDetails = createSelector(
  getCustomTreatmentsState,
  fromCustomTreatment.getShowDetails,
);

// ==== composite methods ====

export const getPatientPlans = createSelector(
  [getSelectedPatient, getPlans],
  (selectedPatient, plans) => {
    if (!selectedPatient) {
      return [];
    }
    return plans.filter((plan) => plan.patient.id === selectedPatient.id);
  },
);

/*
OLD COMMENT
export const getDiagnosisOdontogram = createSelector(
    [getOdontogram, getDiagnosisItems, getDiagnosisCurrentItemFull],
    (odontogram, items, currentItem) => {
        // TODO: only if current item is valid we can add it to the other items
        // if (currentItem.vali)
        return computeOdontogram(odontogram, [...items, currentItem]);
    }
);
*/



// WEBPACK FOOTER //
// /app/src/app/core/store/reducers.ts